import React from 'react';
import { Users, Download, Edit, Star, Copy, FolderInput, Trash2, Info } from 'lucide-react';

interface ContextMenuProps {
  isOpen: boolean;
  onClose?: () => void;
  position: { x: number; y: number };
  isFolder?: boolean;
  isTrashView?: boolean;
  isStarred?: boolean;
  isShared?: boolean;
  isProtected?: boolean;
  onShare: () => void;
  onDownload: () => void;
  onRename: () => void;
  onToggleStar: () => void;
  onDuplicate: () => void;
  onMoveTo: () => void;
  onDelete: () => void;
  onRestore?: () => void;
  onPermanentDelete?: () => void;
  onPreview?: () => void;
  onOpen?: () => void;
  onDetails?: () => void;
  isGlobalDarkMode?: boolean;
}

export const ContextMenu: React.FC<ContextMenuProps> = ({
  isOpen,
  onClose,
  position,
  isTrashView = false,
  isStarred = false,
  isProtected = false,
  onShare,
  onDownload,
  onRename,
  onToggleStar,
  onDuplicate,
  onMoveTo,
  onDelete,
  onRestore,
  onPermanentDelete,
  onDetails,
}) => {
  if (!isOpen) return null;

  let menuItems: any[] = [];

  if (isTrashView) {
    if (isProtected) {
      menuItems = [];
    } else {
      // Trash view menu items
      menuItems = [
        {
          icon: () => (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
            </svg>
          ),
          label: 'Restore',
          onClick: onRestore || (() => {}),
          shortcut: ''
        },
        {
          icon: Trash2,
          label: 'Delete permanently',
          onClick: onPermanentDelete || (() => {}),
          shortcut: '',
          danger: true
        }
      ];
    }
  } else if (isProtected) {
    // Protected / Default folder menu items (cannot be edited, moved, renamed, or deleted)
    menuItems = [
      {
        icon: Users,
        label: 'Share',
        onClick: onShare,
        shortcut: ''
      },
      {
        icon: Download,
        label: 'Download',
        onClick: onDownload,
        shortcut: ''
      },
      { divider: true },
      {
        icon: isStarred ? () => <Star size={16} fill="currentColor" className="text-workspace-accent" /> : Star,
        label: isStarred ? 'Bookmarked' : 'Bookmark',
        onClick: onToggleStar,
        shortcut: ''
      },
      { divider: true },
      {
        icon: Info,
        label: 'Details',
        onClick: onDetails || (() => {}),
        shortcut: ''
      }
    ];
  } else {
    // Normal view menu items
    menuItems = [
      {
        icon: Edit,
        label: 'Rename',
        onClick: onRename,
        shortcut: ''
      },
      {
        icon: FolderInput,
        label: 'Move To',
        onClick: onMoveTo,
        shortcut: ''
      },
      { divider: true },
      {
        icon: Users,
        label: 'Share',
        onClick: onShare,
        shortcut: ''
      },
      {
        icon: Download,
        label: 'Download',
        onClick: onDownload,
        shortcut: ''
      },
      { divider: true },
      {
        icon: Copy,
        label: 'Duplicate',
        onClick: onDuplicate,
        shortcut: ''
      },
      {
        icon: isStarred ? () => <Star size={16} fill="currentColor" className="text-workspace-accent" /> : Star,
        label: isStarred ? 'Bookmarked' : 'Bookmark',
        onClick: onToggleStar,
        shortcut: ''
      },
      { divider: true },
      {
        icon: Info,
        label: 'Details',
        onClick: onDetails || (() => {}),
        shortcut: ''
      },
      { divider: true },
      {
        icon: Trash2,
        label: 'Move to trash',
        onClick: onDelete,
        shortcut: '',
        danger: true
      }
    ];
  }

  return (
    <>
      <div
        className="fixed inset-0 z-40"
        onClick={() => onClose?.()}
      />
      <div
        className="fixed z-50 bg-popover rounded-xl shadow-lg border border-border p-1.5 min-w-[200px]"
        style={{
          left: position.x,
          top: position.y,
          transform: 'translateY(-10px)'
        }}
      >
        {menuItems.map((item, index) => {
          if ('divider' in item) {
            return <div key={index} className="h-px border-t border-border my-1" />;
          }

          const Icon = typeof item.icon === 'function' ? item.icon : item.icon;
          return (
            <button
              key={index}
              onClick={() => {
                item.onClick();
                onClose?.();
              }}
              className={`w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-accent rounded-lg transition-colors duration-150 ${
                item.danger ? 'text-destructive hover:bg-destructive/10' : 'text-foreground'
              }`}
            >
              {typeof Icon === 'function' ? <Icon /> : <Icon size={16} />}
              <span className="flex-1 text-left">{item.label}</span>
              {item.shortcut && (
                <span className="text-xs text-muted-foreground">{item.shortcut}</span>
              )}
            </button>
          );
        })}
      </div>
    </>
  );
};

